import {
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { Dayjs } from 'dayjs'
import { CSSProperties, useMemo } from 'react'

import { useIsHoliday } from 'store/holidaySlice'

import { helper } from '../../../../helper'
import { ApiShiftQuestionnaires } from '../../../../types/shiftQuestionnaire'
import { colors, Icons } from '../../../../variables'

import ShiftLabelComponent from './shiftLabel'

interface Props {
  questionnaires: ApiShiftQuestionnaires
  days: Dayjs[]
}

export default function ResultComponent(props: Props) {
  const isHoliday = useIsHoliday()

  // securityId -> ox
  const answerMap: Map<string, boolean[]> = useMemo(() => {
    const orderedAnswers: [string, boolean[]][] =
      props.questionnaires.securities.map((security) => {
        const answers: boolean[] = Array.from(
          { length: props.days.length },
          () => false,
        )

        security.shiftQuestionnaire?.answers.forEach((answer) => {
          answers[answer.date.date() - 1] = answer.availability
        })

        return [security.id, answers]
      })
    return new Map(orderedAnswers)
  }, [props.questionnaires, props.days])

  return (
    <>
      <TableContainer sx={tableStyle}>
        <Table>
          <TableHead>
            <TableRow style={headerRowStyle}>
              <TableCell style={headerCrossStyle}>{/*空白*/}</TableCell>
              {props.days.map((date) => (
                <TableCell key={`header-${date.date()}`}>
                  <Box
                    style={{
                      ...dateCellStyle,
                      ...weekColorStyle(date, isHoliday(date)),
                    }}
                  >
                    <Box>{date.format('D日')}</Box>
                    <Box>{date.format('(ddd)')}</Box>
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {props.questionnaires.securities.map((security) => {
              const answers = answerMap.get(security.id)
              if (!answers) return null

              return (
                <TableRow key={security.id}>
                  <TableCell style={headerColStyle}>
                    <Typography style={nameCellStyle}>
                      {helper.formatter.nameFormat(security)}
                    </Typography>
                  </TableCell>
                  {props.days.map((date) => (
                    <TableCell key={`row-${security.id}-${date.date()}`}>
                      {answers[date.date() - 1] ? (
                        <ShiftLabelComponent style={answerOkCellStyle}>
                          <Icons.answerOk />
                        </ShiftLabelComponent>
                      ) : (
                        <ShiftLabelComponent style={answerNgCellStyle}>
                          <Icons.answerNg />
                        </ShiftLabelComponent>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  )
}

const tableBorder = '1px solid #B4BEBE'
const tableStyle: SxProps = {
  overflow: 'scroll',
  '& table': {
    borderCollapse: 'separate',
  },

  // === 罫線 ===
  '& td, th': {
    border: 'none',
  },

  '& th': {
    borderTop: tableBorder,
    borderBottom: tableBorder,
  },

  '& tr:not(:first-of-type) td': {
    borderTop: tableBorder,
  },

  '& :is(td, th):not(:last-of-type)': {
    borderRight: tableBorder,
  },
}

const headerCrossStyle: CSSProperties = {
  position: 'sticky',
  top: 0,
  left: 0,
  backgroundColor: '#F7F8F8',
  zIndex: 3,
}

const headerRowStyle: CSSProperties = {
  position: 'sticky',
  top: 0,
  backgroundColor: '#F7F8F8',
  zIndex: 2,
}

const headerColStyle: CSSProperties = {
  position: 'sticky',
  left: 0,
  backgroundColor: 'white',
  zIndex: 1,
}

const nameCellStyle: CSSProperties = {
  fontSize: '16px',
  whiteSpace: 'nowrap',
  width: 130,
  paddingLeft: '8px',
}

const dateCellStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  fontSize: '12px',
  alignItems: 'center',
  lineHeight: '1em',
}

const answerOkCellStyle: CSSProperties = {
  color: '#5F6E6E',
  backgroundColor: '#B8E9E7',
}

const answerNgCellStyle: CSSProperties = {
  color: 'white',
  backgroundColor: '#AFB6B6',
}

const weekColorStyle = (date: Dayjs, isHoliday: boolean): CSSProperties => {
  if (date.day() === 0 || isHoliday) {
    return {
      // 日曜日
      color: colors.WEEK.SUN_COLOR,
      backgroundColor: colors.WEEK.SUN_BG_COLOR,
    }
  }

  if (date.day() === 6) {
    return {
      // 土曜日
      color: colors.WEEK.SAT_COLOR,
      backgroundColor: colors.WEEK.SAT_BG_COLOR,
    }
  }

  return {
    // 平日 (色の上書き無し)
  }
}
